import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { ConnectionStatusService } from '@core/services/connection-status.service';
import { IaraContextService } from '@core/services/iara-context/iara-context.service';
import {
  PALANTIR_CHAT_PERSONA_IARA,
  PalantirAvatarComponent,
  PalantirChatComponent,
  type PalantirChatHeaderAction,
  PalantirIaraCalloutComponent,
  PalantirIaraConnectionBannerComponent,
  PalantirIaraConversationListComponent,
  type PalantirIaraConversation,
  PalantirIaraOrbComponent,
  PalantirIaraReasoningComponent,
  PalantirIaraTurnActionsComponent,
} from '@flux/design-system';
import { CHAT_PROMPT_CONTEXT_ENTITIES } from '@flux/iara-core/prompt-context';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { CHAT_SESSION_SEARCH_DEBOUNCE_MS } from '../../../constants/chat-session-display';
import { toIaraConversation } from '../../../utils/to-iara-conversation';
import { IARA_LAUNCHER_LAYOUT } from '../../constants/iara-launcher-positions';
import {
  IARA_PANEL_HEADER_ACTIONS,
  type IaraPanelHeaderActionId,
} from '../../constants/iara-panel-header-actions';
import { ChatComponentHostComponent } from '../../../components/chat-component-host/chat-component-host.component';
import { ChatMarkdownComponent } from '../../../components/chat-markdown/chat-markdown.component';
import type { ChatComponent } from '../../../models/chat.model';
import { ChatService } from '../../../services/chat.service';
import {
  IARA_WELCOME_TITLE,
  IARA_WELCOME_TITLE_FALLBACK,
} from '../../constants/iara-welcome-titles';
import { IaraPanelStateService } from '../../services/iara-panel-state.service';
import { toPalantirChatMessage } from '../../utils/to-palantir-chat-message';
import { toSendMessageContext } from '../../utils/to-send-message-context';

/**
 * Superfície de conversa do painel flutuante, montada sobre o `palantir-chat` com
 * a persona da Iara.
 *
 * Separado do host de propósito: é AQUI que `ChatService` é provido, e o construtor
 * dele dispara queries autenticadas. O host só cria este componente depois de
 * confirmar que o painel está disponível e foi aberto — ver o comentário em
 * `IaraFloatingChatComponent`.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChatComponentHostComponent,
    ChatMarkdownComponent,
    PalantirAvatarComponent,
    PalantirChatComponent,
    PalantirIaraCalloutComponent,
    PalantirIaraConnectionBannerComponent,
    PalantirIaraConversationListComponent,
    PalantirIaraOrbComponent,
    PalantirIaraReasoningComponent,
    PalantirIaraTurnActionsComponent,
  ],
  providers: [ChatService],
  selector: 'app-iara-floating-chat-panel',
  standalone: true,
  templateUrl: './iara-floating-chat-panel.component.html',
})
export class IaraFloatingChatPanelComponent {
  protected readonly service = inject(ChatService);
  protected readonly connection = inject(ConnectionStatusService);
  protected readonly panel = inject(IaraPanelStateService);

  protected readonly persona = PALANTIR_CHAT_PERSONA_IARA;
  protected readonly headerActions = IARA_PANEL_HEADER_ACTIONS;

  /**
   * Mesmo mapa do gatilho, então o painel segue o botão — inclusive a pré-visualização
   * durante o arraste, que é o que responde "cabe ali?" antes de comitar.
   */
  protected readonly layout = computed(() => IARA_LAUNCHER_LAYOUT[this.panel.effectivePosition()]);

  private readonly auth = inject(AuthService);
  private readonly iaraContext = inject(IaraContextService);
  private readonly router = inject(Router);

  /**
   * Nome do usuário autenticado. Vai como `fallbackText` do `palantir-avatar`, que
   * reduz o texto a iniciais por conta própria — inclusive quando a imagem existe
   * mas falha ao carregar (ele escuta o `error` do `<img>`).
   */
  protected readonly userName = computed(() => this.auth.authenticatedUser()?.user?.name ?? '');

  protected readonly userAvatarUrl = computed(
    () => this.auth.authenticatedUser()?.user?.avatarUrl ?? null,
  );

  /** Descartar o contexto o desliga até o painel ser fechado e reaberto. */
  private readonly contextDismissed = signal(false);

  /** Gaveta de histórico. Two-way com o `palantir-chat`, que também a alterna. */
  protected readonly historyOpen = signal(false);

  private readonly sessionSearch = new Subject<string>();

  protected readonly conversations = computed<PalantirIaraConversation[]>(() =>
    this.service.sessions().map((session) => toIaraConversation(session, this.service.agents())),
  );

  /**
   * O `palantir-chat` também recebe as sessões porque a gaveta EMBUTIDA dele é o
   * fallback; com `sessionsTemplate` informado quem manda é a lista projetada.
   */
  protected readonly chatSessions = computed(() =>
    this.service.sessions().map((session) => ({ id: session.id, title: session.title })),
  );

  protected readonly context = computed(() =>
    this.contextDismissed() ? { entities: [] } : this.iaraContext.context(),
  );

  protected readonly chatMessages = computed(() =>
    this.service.messages().map((message) => toPalantirChatMessage(message)),
  );

  /** A última entidade é a mais específica: fluxo → card → etapa → tarefa/aprovação. */
  private readonly primaryEntity = computed(
    () =>
      this.context()
        .entities.filter((entity) => entity.id ?? entity.label)
        .at(-1) ?? null,
  );

  protected readonly hasContext = computed(() => this.primaryEntity() !== null);

  /**
   * Rótulo do badge de contexto do `palantir-chat`. Prefixa o tipo porque o badge
   * do DS tem ícone fixo — sem o prefixo, "Troca de roteador" não diria que é card.
   */
  protected readonly contextLabel = computed(() => {
    const entity = this.primaryEntity();
    if (!entity) return null;

    const { label: type } = CHAT_PROMPT_CONTEXT_ENTITIES[entity.type];
    return entity.label ? `${type}: ${entity.label}` : type;
  });

  /**
   * Sobrepõe o `welcomeTitle` da persona para anunciar a entidade da tela. É o
   * override granular do `palantir-chat` em uso: a persona dá o texto padrão, e o
   * painel o especializa pelo contexto.
   */
  protected readonly welcomeTitle = computed(() => {
    const entity = this.primaryEntity();

    return entity ? IARA_WELCOME_TITLE[entity.type] : IARA_WELCOME_TITLE_FALLBACK;
  });

  public constructor() {
    // Publica qual conversa está na frente do usuário, para o aviso global "a Iara
    // terminou" não notificar quem está vendo a resposta chegar. O painel segue
    // MONTADO quando fechado (é assim que a conversa sobrevive), então "aberto" faz
    // parte da conta — e ao ser destruído nada mais está visível.
    effect(() => {
      this.panel.publishVisibleSession(this.panel.isOpen() ? this.service.activeSessionId() : null);
    });

    inject(DestroyRef).onDestroy(() => this.panel.publishVisibleSession(null));

    // Debounce igual ao da `/chat`: a busca é server-side e o serviço não faz por conta.
    //
    // A dedução de repetido compara com o termo que o SERVIÇO guarda, não com a última
    // emissão do Subject. `distinctUntilChanged` lembraria da própria história e ficaria
    // dessincronizado do serviço, porque o reset ao abrir a gaveta chama
    // `searchSessions('')` direto — então redigitar o mesmo termo de antes era suprimido
    // e a lista ficava sem filtro com o campo preenchido. Uma fonte de verdade só.
    this.sessionSearch
      .pipe(debounceTime(CHAT_SESSION_SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe((term) => {
        // `searchSessions` guarda o termo já trimado; comparar sem trimar reintroduziria
        // a divergência para quem digita espaço no fim.
        if (term.trim() === this.service.sessionSearch()) return;

        void this.service.searchSessions(term);
      });

    // Fechar o painel fecha a gaveta e devolve o contexto. Cobre os DOIS caminhos de
    // fechamento — o X do cabeçalho e a pill —, e só o primeiro passa por `onClose()`.
    effect(() => {
      if (this.panel.isOpen()) return;

      this.historyOpen.set(false);
      this.contextDismissed.set(false);
    });

    // Abrir a gaveta zera a busca. Não é cosmético: fechar destrói a lista projetada e
    // com ela o campo de busca, enquanto o serviço guardaria o termo antigo — reabrir
    // mostraria campo vazio com lista filtrada.
    effect(() => {
      if (this.historyOpen()) void this.service.searchSessions('');
    });
  }

  protected asChatComponent(value: unknown): ChatComponent {
    return value as ChatComponent;
  }

  protected onClose(): void {
    // O reset de contexto e da gaveta vive no `effect` que observa `panel.isOpen()`,
    // para valer também quando o fechamento vem da pill.
    this.panel.close();
  }

  protected onSend(content: string): void {
    // O retorno (id da sessão nova) é descartado de propósito: o painel não empurra
    // URL — quem quiser continuar em `/chat` usa "abrir no chat completo".
    void this.service.send(content, toSendMessageContext(this.context()));
  }

  protected onRetry(messageId?: string): void {
    if (messageId) void this.service.regenerate(messageId);
  }

  /**
   * Nova conversa é reset local (`newConversation()` não faz request). Sem diálogo de
   * confirmação: a conversa anterior está persistida no servidor e fica a um clique na
   * gaveta, então não há o que confirmar.
   */
  protected onNewConversation(): void {
    // Em streaming, interrompe antes de resetar. Abandonar o turno em silêncio gastaria
    // crédito e dispararia o aviso global "a Iara terminou" para uma conversa largada.
    // `stop()` captura o id da mensagem de forma síncrona, então não precisa de `await`.
    if (this.service.isStreaming()) void this.service.stop();

    this.historyOpen.set(false);
    this.service.newConversation();
    this.contextDismissed.set(false);
  }

  protected onSelectSession(conversation: PalantirIaraConversation): void {
    // Quem fecha a gaveta é o consumidor: `openSession` é no-op quando a conversa já
    // está ativa, então o DS nunca saberia que houve escolha.
    this.historyOpen.set(false);
    void this.service.openSession(conversation.id);
  }

  protected onSearchSessions(term: string): void {
    this.sessionSearch.next(term);
  }

  protected onLoadMoreSessions(): void {
    void this.service.loadMoreSessions();
  }

  protected onRenameSession(event: {
    conversation: PalantirIaraConversation;
    title: string;
  }): void {
    void this.service.renameSession(event.conversation.id, event.title);
  }

  protected onDeleteSession(conversation: PalantirIaraConversation): void {
    void this.service.deleteSession(conversation.id);
  }

  protected onDismissContext(): void {
    this.contextDismissed.set(true);
  }

  /**
   * Compara o `id`, não o rótulo: rótulo é texto de interface e muda sem que o
   * comportamento mude.
   *
   * O parâmetro é o tipo base porque o output do `palantir-chat` emite
   * `PalantirChatHeaderAction<string>` — quem garante que só existem ids válidos é a
   * declaração de `IARA_PANEL_HEADER_ACTIONS`, tipada com a união.
   */
  protected onHeaderAction(action: PalantirChatHeaderAction): void {
    const id = action.id as IaraPanelHeaderActionId;

    if (id === 'expand') this.onOpenFullChat();
  }

  protected onOpenFullChat(): void {
    const sessionId = this.service.activeSessionId();
    this.panel.close();

    void this.router.navigate(sessionId ? ['/chat', sessionId] : ['/chat']);
  }
}
